import { supabase } from '../lib/supabase'

const SCREENSHOT_BUCKET = 'support-screenshots'
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown'

// ─── Helpers ────────────────────────────────────────────────────────────────

const currentUserContext = () => ({
  page_url: typeof window !== 'undefined' ? window.location.href : null,
  user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  app_version: APP_VERSION,
})

const uploadScreenshot = async (userId, ticketId, blob, extension) => {
  const safeExt = (extension || 'jpg').replace(/[^a-z0-9]/gi, '').toLowerCase() || 'jpg'
  const path = `${userId}/${ticketId}.${safeExt}`
  const contentType = blob.type || `image/${safeExt === 'jpg' ? 'jpeg' : safeExt}`

  const { error } = await supabase.storage
    .from(SCREENSHOT_BUCKET)
    .upload(path, blob, { contentType, upsert: true })

  if (error) throw error

  return path
}

const invokeFn = async (name, body) => {
  try {
    await supabase.functions.invoke(name, { body })
  } catch (err) {
    // Notifications are best-effort - the ticket / reply itself has already
    // persisted. Surface in console so we notice in dev, but don't fail the
    // user's submit.
    console.error(`[supportService] ${name} invoke failed:`, err)
  }
}

// ─── User-facing API ────────────────────────────────────────────────────────

export const createSupportTicket = async ({ category, subject, message, screenshot }) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const ctx = currentUserContext()

  // Generate the ticket id client-side so we can upload the screenshot
  // BEFORE inserting the row. The RLS update policy on support_tickets
  // is admin-only, so a post-insert UPDATE to set screenshot_url would
  // silently no-op for the submitting user (file in storage, but column
  // stays null and the admin view never renders the image).
  // crypto.randomUUID is available everywhere we care about
  // (modern browsers, Capacitor WKWebView, Node ≥ 16); the column accepts
  // a client-provided uuid because Postgres' default is `gen_random_uuid()`
  // (a default — not a check constraint).
  const ticketId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : undefined

  let screenshotUrl = null
  if (screenshot?.blob) {
    try {
      screenshotUrl = await uploadScreenshot(user.id, ticketId, screenshot.blob, screenshot.ext)
    } catch (err) {
      // Non-fatal — submit the ticket without the screenshot rather than
      // blocking the user. We log so this surfaces in dev / Xcode console.
      console.error('[supportService] screenshot upload failed:', err)
    }
  }

  const insertPayload = {
    user_id: user.id,
    category,
    subject,
    page_url: ctx.page_url,
    user_agent: ctx.user_agent,
    app_version: ctx.app_version,
    screenshot_url: screenshotUrl,
  }
  if (ticketId) insertPayload.id = ticketId

  const { data: ticket, error: ticketErr } = await supabase
    .from('support_tickets')
    .insert(insertPayload)
    .select()
    .single()

  if (ticketErr) throw ticketErr

  // First message in the thread = the initial submission body.
  const { error: msgErr } = await supabase
    .from('support_ticket_messages')
    .insert({
      ticket_id: ticket.id,
      sender_id: user.id,
      sender_role: 'user',
      body: message,
    })

  if (msgErr) throw msgErr

  await invokeFn('send-support-ticket', { ticket_id: ticket.id })

  return ticket
}

export const listMyTickets = async () => {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('id, category, subject, status, screenshot_url, unread_for_user, last_message_at, created_at')
    .order('last_message_at', { ascending: false })

  if (error) throw error
  return data || []
}

export const getTicketThread = async (ticketId) => {
  const [ticketRes, messagesRes] = await Promise.all([
    supabase.from('support_tickets').select('*').eq('id', ticketId).single(),
    supabase
      .from('support_ticket_messages')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true }),
  ])

  if (ticketRes.error) throw ticketRes.error
  if (messagesRes.error) throw messagesRes.error

  return { ticket: ticketRes.data, messages: messagesRes.data || [] }
}

export const postReply = async ({ ticketId, body }) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('support_ticket_messages')
    .insert({
      ticket_id: ticketId,
      sender_id: user.id,
      sender_role: 'user',
      body,
    })
    .select()
    .single()

  if (error) throw error

  await invokeFn('send-support-ticket-reply', { message_id: data.id })

  return data
}

export const markTicketRead = async (ticketId) => {
  const { data, error } = await supabase.rpc('mark_support_ticket_read', { p_ticket_id: ticketId })
  if (error) throw error
  return data
}

export const getUnreadForUserCount = async () => {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('id', { count: 'exact', head: true })
    .gt('unread_for_user', 0)

  if (error) throw error
  return count || 0
}

// Public download URL for a screenshot. Bucket is private, so we sign briefly.
export const getScreenshotUrl = async (path, expiresIn = 60 * 30) => {
  if (!path) return null
  const { data, error } = await supabase.storage
    .from(SCREENSHOT_BUCKET)
    .createSignedUrl(path, expiresIn)
  if (error) {
    console.error('[supportService] signed url failed:', error)
    return null
  }
  return data?.signedUrl || null
}

// ─── Admin API ──────────────────────────────────────────────────────────────

export const adminListTickets = async ({ status } = {}) => {
  let query = supabase
    .from('support_tickets')
    .select('id, user_id, category, subject, status, screenshot_url, unread_for_admin, last_message_at, created_at')
    .order('last_message_at', { ascending: false })

  if (status && status !== 'all') {
    query = query.eq('status', status)
  }

  const { data, error } = await query
  if (error) throw error
  return data || []
}

export const adminGetTicket = async (ticketId) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('id', ticketId)
    .single()
  if (error) throw error
  return data
}

export const adminUpdateStatus = async (ticketId, status) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({ status })
    .eq('id', ticketId)
    .select()
    .single()
  if (error) throw error
  return data
}

export const adminSetNotes = async (ticketId, notes) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({ admin_notes: notes })
    .eq('id', ticketId)
    .select()
    .single()
  if (error) throw error
  return data
}

export const adminPostReply = async ({ ticketId, body }) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('support_ticket_messages')
    .insert({
      ticket_id: ticketId,
      sender_id: user.id,
      sender_role: 'admin',
      body,
    })
    .select()
    .single()

  if (error) throw error

  await invokeFn('send-support-ticket-reply', { message_id: data.id })

  return data
}

export const adminGetUnreadCount = async () => {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('id', { count: 'exact', head: true })
    .gt('unread_for_admin', 0)
  if (error) throw error
  return count || 0
}
